import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import apiClient from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "./ui/form";
import { useToast } from "../hooks/use-toast";
import { motion } from "framer-motion";
import { Check, Loader2, Building2, ArrowLeft, Mail } from "lucide-react";
import bgImage from "../assets/auth-background.png";

const forgotPasswordSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPassword() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'sent'>('email');
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');

  const form = useForm<ForgotPasswordFormValues>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(data: ForgotPasswordFormValues) {
    setIsLoading(true);
    setError('');
    setEmail(data.email);
    try {
      await apiClient.auth.requestPasswordReset(data.email);
      setStep('sent');
      toast({
        title: "Check your email",
        description: "If an account exists, we sent a link to reset your password.",
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to send reset. Please try again.';
      setError(errorMessage);
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  }


  return (
    <div className="min-h-screen w-full flex overflow-hidden">
      {/* Left Side - Form Area */}
      <div className="w-full lg:w-1/2 flex flex-col bg-background relative z-10">
        <div className="flex-1 flex flex-col justify-center items-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-6">
            <div className="text-center lg:text-left">
              {/* Logo */}
              <Link to="/">
                <span className="text-2xl font-bold text-primary tracking-tight inline-flex items-center gap-2 cursor-pointer mb-8">
                  <Building2 className="h-8 w-8" />
                  Cityscrape
                </span>
              </Link>

              {/* Header */}
              <h1 className="text-3xl font-bold tracking-tight text-foreground mb-2">
                Forgot password?
              </h1>
              <p className="text-muted-foreground">
                No worries, we'll send you reset instructions.
              </p>

              {error && (
                <div className="bg-destructive/15 text-destructive px-3 py-2 rounded-md text-sm mt-4">
                  {error}
                </div>
              )}

              {step === 'email' && (
                <Form {...form}>
                  <form
                    onSubmit={form.handleSubmit(onSubmit)}
                    className="space-y-5 mt-8"
                  >
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input placeholder="name@company.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full h-11 font-medium text-base"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        "Send Reset Link"
                      )}
                    </Button>
                  </form>
                </Form>
              )}

              {step === 'sent' && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="space-y-5 mt-8 text-center"
                >
                  <div className="mx-auto w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mb-4">
                    <Check className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg">Check your email</h3>
                  <p className="text-sm text-muted-foreground">
                    If an account exists for <span className="font-medium text-foreground">{email}</span>, we sent a link to reset your password.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => { setStep('email'); setError(''); }}
                  >
                    Send another link
                  </Button>
                </motion.div>
              )}

              <div className="pt-6 text-center">
                <Link to="/login">
                  <span className="inline-flex items-center justify-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to login
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Content */}
      <div className="hidden lg:flex w-1/2 relative bg-muted text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={bgImage}
            alt="Background"
            className="w-full h-full object-cover opacity-40 mix-blend-multiply filter grayscale"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-900/80 to-neutral-900/90 mix-blend-multiply" />
          <div className="absolute inset-0 bg-primary/10 mix-blend-overlay" />
        </div>

        <div className="relative z-10 flex flex-col justify-center w-full p-12 xl:p-20 h-full">
          <div className="space-y-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <h2 className="text-4xl xl:text-5xl font-bold leading-tight mb-6 text-white">
                Speed and Clarity for Every Project
              </h2>
              <p className="text-lg text-neutral-300 max-w-md leading-relaxed">
                Instant access to open, unified planning information so you can
                make faster, clearer, more informed development and acquisition
                decisions.
              </p>
            </motion.div>

            <div className="space-y-4 pt-4">
              {[
                "Save Time on Research",
                "Stay Ahead of Policy Changes",
                "Reduce Project Risk",
                "Make Faster Decisions",
              ].map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                  className="flex items-center gap-3 text-neutral-200"
                >
                  <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center text-primary border border-primary/30">
                    <Check className="h-3.5 w-3.5" />
                  </div>
                  <span className="font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
